import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import Api from '@/services/api';
import { Charts } from './Charts';
import { References } from './References';
import { useFetch } from '@/hooks/useFetch';
import { Options } from '../Components/Options';
import { FileData } from '../Components/FileData';
import { formatSecondsDuration } from '@/util/mask';
import { Conclusion } from '../Components/Conclusion';
import { JobInformations } from '../Components/JobInformations';
import { setRiskDegree, setData, prev } from '@/redux/reports/actions';
import { useApplicationContext } from '@/context/v1/Application/context';
import { collectionDateParse, fileDurationParse, fileSizeFormat, sectorJSON } from '@/redux/reports/selectors';
import { RiskDegreeResult, RiskDegreeError } from './styles';
import { SendReportButton } from '@/components/ui/Buttons/SendReportButton';
import { SendReportModal } from '@/components/ui/Modals/SendReportModal';

const antIcon = <LoadingOutlined style={{ fontSize: 20 }} spin />;

const Container = styled(Row)`
	padding: 25px 10px 10px 10px;
`;

export function Diagnostic() {
	const dispatch = useDispatch();
	const { organization, company, isSandbox, sendReport, existsReport } = useApplicationContext();
	const [isSendReportModalOpen, setIsSendReportModalOpen] = useState(false);

	const [loading, setLoading] = useState(false);

	const { parameters, informations } = useSelector((state) => state.reports.customized);

	const file = useSelector((state) => state.reports.file);
	const sector = useSelector(sectorJSON);
	const fileSize = useSelector(fileSizeFormat);
	const fileDuration = useSelector(fileDurationParse);
	const collectionDate = useSelector(collectionDateParse);

	const data = useSelector((state) => state.reports.data);
	const options = useSelector((state) => state.reports.options);
	const user = useSelector((state) => state.auth.user.user);

	const { companyInfo } = useSelector(
		(state) => ({
			companyInfo: state.reports.customized.informations.company
		}),
		shallowEqual
	);

	function handleOpenSendReportModal() {
		setIsSendReportModalOpen(true);
	}

	function handleCloseSendReportModal() {
		setIsSendReportModalOpen(false);
	}

	const {
		data: riskDegree,
		error: riskDegreeError,
		isFetching: riskDegreeLoading
	} = useFetch({
		url: '/report/risk_degree/workstation',
		method: 'post',
		body: {
			organization_id: organization.id,
			company_id: company.id,
			file_id: file.id,
			legs: parameters.legs,
			force: parameters.force,
			repetition: parameters.legs
		}
	});

	const {
		data: bodyParts,
		error: bodyPartsError,
		isFetching: bodyPartsLoading
	} = useFetch({
		url: '/report/rula/risk_degree/body_parts',
		method: 'post',
		body: {
			organization_id: organization.id,
			company_id: company.id,
			file_id: file.id
		}
	});

	const exists = existsReport({
		report_type: 'RECOVERY',
		file_id: file.id || '',
		company_id: company?.id,
		organization_id: organization?.id
	});

	function handleSendReport() {
		const request = {
			report_type: 'RECOVERY',
			id: riskDegree?.id || '',
			company_id: company?.id,
			organization_id: organization?.id
		};
		sendReport(request, '/reporting');
	}

	function mountSendReportModalProps() {
		const report = {
			id: riskDegree?.id || '',
			workstation_name: file.workstations.name || '',
			tools_to_show: [],
			action_plans: [],
			files_names: [file?.original_name || '']
		};
		return {
			isOpen: isSendReportModalOpen,
			onCancel: handleCloseSendReportModal,
			onConfirm: handleSendReport,
			report: report,
			exists: exists
		};
	}

	useEffect(() => {
		if (riskDegree) {
			dispatch(setRiskDegree(riskDegree));
		}
		if (bodyParts) {
			dispatch(setData(bodyParts));
		}
	}, [riskDegree, bodyParts]);

	const RiskDegreeStatus = ({ score }) => {
		let isResult = { type: '', description: I18n.get('Not found') };

		if (score >= 1 && score <= 2) {
			isResult = { type: 'safe', description: I18n.get('Acceptable') };
		}

		if (score > 2 && score <= 4) {
			isResult = { type: 'info', description: I18n.get('Light') };
		}

		if (score > 4 && score <= 6) {
			isResult = { type: 'warning', description: I18n.get('Average') };
		}

		if (score > 6 && score >= 7) {
			isResult = { type: 'danger', description: I18n.get('High') };
		}

		return <RiskDegreeResult type={isResult.type}>{I18n.get(isResult.description)}</RiskDegreeResult>;
	};

	const Title = ({ children }) => (
		<Col sm={24} style={{ paddingBottom: '15px' }}>
			<h2>{I18n.get(children)}</h2>
		</Col>
	);

	const ExposureAverage = () => (
		<Col sm={24} style={{ paddingTop: '20px' }}>
			<Row type="flex" align="middle" gutter={[8, 0]} style={{ alignItems: 'center' }}>
				<Col>
					<h2>
						{I18n.get('Average exposure of the workplace')}:{' '}
						{riskDegreeLoading && <Spin indicator={antIcon} />}
					</h2>
				</Col>
				<Col>
					{riskDegree && <RiskDegreeStatus score={riskDegree.score} />}
					{riskDegreeError && <RiskDegreeError>{I18n.get('Failed to fetch data')}</RiskDegreeError>}
				</Col>
			</Row>
		</Col>
	);

	function onPrevious() {
		dispatch(prev('customized'));
	}

	function onSuccessResponse(response) {
		const blob = new Blob([response.data], { type: 'application/pdf' });
		window.open(URL.createObjectURL(blob));
	}

	function onErrorResponse(err) {
		console.log(err, 'err');
	}

	function normalizeData() {
		const sector = JSON.parse(informations.sector);
		const durationFormated = formatSecondsDuration(file.duration);
		const browserLanguage = window.navigator.language.substring(0, 2) ?? 'en';

		return {
			tool: 'rula',
			document_type: 'pdf',
			report: 'score_parts',
			language: browserLanguage,
			parameters: {
				risk_degree: riskDegree?.score || null,
				data: data,
				sector_name: sector.name,
				line_name: file.line.name,
				configuration: 'Default',
				collection_date: collectionDate,
				company_name: informations.company,
				workstation_name: file.workstations.name,
				repetition: parameters.repetition,
				file: {
					name: file.original_name,
					duration: durationFormated,
					size: file.size
				},
				signature: options.signature.checked && {
					author: user.name
				},
				conclusion: options.conclusion.checked && {
					text: options.conclusion.text
				}
			}
		};
	}

	function onFinish() {
		const url = '/report/document';
		const body = normalizeData();
		const opts = { responseType: 'blob' };

		Api.post(url, body, opts)
			.then(setLoading(true))
			.then(onSuccessResponse)
			.catch(onErrorResponse)
			.finally(() => setLoading(false));
	}

	const Footer = () => (
		<Col sm={24} style={{ paddingTop: '15px' }}>

			<Row justify="end">
				{isSandbox && riskDegree?.id && <SendReportButton onClick={handleOpenSendReportModal} style={{ height: "34px", fontSize: "14px" }} />}
				<Col>
					<Button style={{ margin: '0 8px' }} onClick={onPrevious}>
						{I18n.get('Previous')}
					</Button>
				</Col>
				<Col>
					<Button type="primary" loading={loading} onClick={onFinish}>
						{I18n.get('Finish')}
					</Button>
				</Col>
			</Row>
		</Col>
	);

	return (
		<>
			{isSandbox && isSendReportModalOpen ? <SendReportModal {...mountSendReportModalProps()} /> :
				<Container gutter={[10, 10]}>
					<Title>{I18n.get('Analysis results')}</Title>
					<JobInformations
						rangeRisk="RULA"
						company={companyInfo}
						sector={sector?.name}
						collectionDate={collectionDate}
						repetition={parameters.repetition}
						line={file.workstations.line?.name}
						workstation={file.workstations.name}
					/>
					<FileData originalName={file.original_name} duration={fileDuration} size={fileSize} />
					<ExposureAverage />
					<Charts loading={bodyPartsLoading} err={bodyPartsError} data={bodyParts} />
					<References />
					<Options />
					<Conclusion />
					<Footer />
				</Container>
			}

		</>
	);
}
